
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Clock, MapPin, Users, Share, Trash2, Plus, Eye, BarChart3, Home, User, Building, ClipboardList, Settings, Edit } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useEvents } from "@/hooks/useEvents";
import { useSubscription } from "@/hooks/useSubscription";
import { useClients } from "@/hooks/useClients";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import NavigationBar from "@/components/NavigationBar";
import { Event } from "@/types/event";
import { toast } from "sonner";
import { EventImageGenerator } from "@/components/EventImageGenerator";
import ClientManagement from "@/components/ClientManagement";
import { ProfessionalTemplateManager } from "@/components/ProfessionalTemplateManager";
import { CustomRSVPManager } from "@/components/CustomRSVPManager";
import { EventClientAssignment } from "@/components/EventClientAssignment";
import { DashboardQuickStats } from "@/components/DashboardQuickStats";
import { DashboardEmptyState } from "@/components/DashboardEmptyState";
import { UnifiedCalendar } from "@/components/UnifiedCalendar";
import { ClientFeedbackManager } from "@/components/ClientFeedbackManager";
import { ClientActivityTimeline } from "@/components/ClientActivityTimeline";

const Dashboard = () => {
  const { user } = useAuth();
  const { createdEvents, invitedEvents, loading, deleteEvent } = useEvents();
  const { 
    checkSubscription, 
    subscription,
    hasClientPortalAccess,
    hasAdvancedAnalytics,
    hasUnlimitedCustomRSVP,
    hasBasicCustomRSVP,
    hasCustomBranding,
    hasAdvancedBranding,
    checkCanCreateEvent,
    createCheckout
  } = useSubscription();
  const { clients } = useClients();
  const navigate = useNavigate();
  const { toast: toastHook } = useToast();
  const [searchParams] = useSearchParams();
  const [selectedEventForRSVP, setSelectedEventForRSVP] = useState<string | null>(null);

  useEffect(() => {
    // Check for success/cancel parameters from Stripe redirect
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (success === 'true') {
      toast.success('Subscription activated successfully!');
      checkSubscription(); // Refresh subscription status
    } else if (canceled === 'true') {
      toast.info('Subscription canceled. You can try again anytime.');
    }
  }, [searchParams, checkSubscription]);

  const handleShareEvent = (event: Event) => {
    const shareUrl = `https://www.eventdatepicker.com/vote/${event.shareableLink}`;
    navigator.clipboard.writeText(shareUrl);
    toastHook({
      title: "Link copied!",
      description: "Event link has been copied to your clipboard",
    });
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      await deleteEvent(eventId);
      toastHook({
        title: "Event deleted",
        description: "Your event has been deleted successfully",
      });
    } catch (error) {
      toastHook({
        title: "Error",
        description: "Failed to delete event",
        variant: "destructive",
      });
    }
  };

  const getEventStatusBadge = (event: Event, isOwner = false) => {
    if (!event.isActive) {
      return <Badge variant="secondary">Closed</Badge>;
    }
    if (event.deadline && new Date(event.deadline) < new Date()) {
      return <Badge variant="destructive">Expired</Badge>;
    }
    
    // For invited events (not owner), check if current user has voted
    if (!isOwner && user?.email) {
      const currentGuest = event.guests.find(guest => guest.email === user.email);
      if (currentGuest?.hasVoted) {
        return <Badge className="bg-green-600 hover:bg-green-700 text-white">Voted</Badge>;
      }
    }
    
    return <Badge variant="default">Active</Badge>;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString();
  };

  const getClientName = (clientId: string) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Unknown Client';
  };

  const handleCreateEvent = async () => {
    const result = checkCanCreateEvent();
    console.log('🔍 [Dashboard] Create event validation result:', result);
    
    if (!result.canCreate) {
      // Enhanced error message promoting Family plan
      const currentTier = subscription.subscription_tier || 'free';
      const currentEvents = subscription.monthlyEvents || 0;
      
      toast.error(
        `🚫 Monthly Event Limit Reached`,
        {
          description: (
            <div className="space-y-3">
              <p className="font-medium text-base">You've created {currentEvents} events this month. Free accounts are limited to 3 events per month.</p>
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/50 dark:to-purple-950/50 p-6 rounded-xl border shadow-sm">
                <p className="font-bold text-lg text-blue-700 dark:text-blue-300 mb-2">🎉 Upgrade to Family Plan for:</p>
                <ul className="text-base font-medium text-blue-600 dark:text-blue-400 space-y-1">
                  <li>• ✨ 20 events per month</li>
                  <li>• 👥 Up to 100 guests per event</li>
                  <li>• 🏠 Family group management</li>
                  <li>• 📊 Basic analytics</li>
                </ul>
              </div>
              <p className="text-base font-medium text-muted-foreground">
                Or try Professional for unlimited events, custom branding, and advanced features!
              </p>
            </div>
          ),
          duration: 8000,
          action: {
            label: "Upgrade Now",
            onClick: () => navigate('/subscription')
          }
        }
      );
      return;
    }
    
    // If validation passes, proceed to create event
    navigate('/create-event');
  };

  const EventCard = ({ event, isOwner = false }: { event: Event; isOwner?: boolean }) => (
    <Card className="hover:shadow-md transition-shadow h-full flex flex-col">
      <CardHeader className="flex-shrink-0 p-4 sm:p-6">
        <div className="flex justify-between items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <CardTitle className="text-base sm:text-lg truncate">{event.name}</CardTitle>
              {event.familyGroupId && (
                <Badge variant="secondary" className="text-xs">
                  <Home className="w-3 h-3 mr-1" />
                  Family
                </Badge>
              )}
              {event.clientId && (
                <Badge variant="outline" className="text-xs">
                  <User className="w-3 h-3 mr-1" />
                  {getClientName(event.clientId)}
                </Badge>
              )}
            </div>
            <CardDescription className="text-xs sm:text-sm">
              Hosted by {event.hostName}
              {event.familyGroupName && (
                <span className="block text-xs text-muted-foreground">
                  {event.familyGroupName}
                </span>
              )}
              {event.deadline && (
                <span className="block mt-1">
                  Deadline: {formatDate(event.deadline)}
                </span>
              )}
            </CardDescription>
          </div>
          <div className="flex-shrink-0 ml-2">
            {getEventStatusBadge(event, isOwner)}
          </div>
        </div>
        {event.description && (
          <p className="text-xs sm:text-sm text-muted-foreground mt-2 line-clamp-2">{event.description}</p>
        )}
        
        {/* Client Assignment - Only show for owners with client portal access */}
        {isOwner && hasClientPortalAccess() && (
          <div className="mt-3">
            <EventClientAssignment 
              eventId={event.id}
              eventTitle={event.name}
              currentClientIds={event.clientId ? [event.clientId] : []}
            />
          </div>
        )}
      </CardHeader>
      <CardContent className="flex-1 flex flex-col p-4 sm:p-6">
        <div className="space-y-3 flex-1">
          {/* Event Options Summary */}
          <div className="flex flex-wrap gap-2">
            {event.options.filter(opt => opt.type === 'date').length > 0 && (
              <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                {event.options.filter(opt => opt.type === 'date').length} dates
              </div>
            )}
            {event.options.filter(opt => opt.type === 'time').length > 0 && (
              <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                {event.options.filter(opt => opt.type === 'time').length} times
              </div>
            )}
            {event.options.filter(opt => opt.type === 'location').length > 0 && (
              <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                {event.options.filter(opt => opt.type === 'location').length} locations
              </div>
            )}
          </div>

          {/* Voting Stats */}
          <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm">
            <div className="flex items-center gap-1">
              <Users className="w-3 h-3 sm:w-4 sm:h-4" />
              {event.guests.length} invited
            </div>
            <div className="text-muted-foreground">
              {event.totalVoters} voted
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-1.5 sm:gap-2 pt-3 sm:pt-4 mt-auto">
          <Button 
            variant="outline" 
            size="sm"
            className="min-h-[40px] sm:min-h-0"
            onClick={() => navigate(`/vote/${event.shareableLink}`)}
          >
            <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
            <span className="text-xs sm:text-sm">View</span>
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            className="min-h-[40px] sm:min-h-0"
            onClick={() => handleShareEvent(event)}
          >
            <Share className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
            <span className="text-xs sm:text-sm">Share</span>
          </Button>
          {isOwner && (
            <>
              {/* Manage button - available for all users */}
              <Button 
                variant="outline" 
                size="sm"
                className="min-h-[40px] sm:min-h-0"
                onClick={() => navigate(`/event-detail/${event.id}`)}
              >
                <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="text-xs sm:text-sm">Manage</span>
              </Button>
              <EventImageGenerator event={event} />
              <Button 
                variant="outline" 
                size="sm"
                className="min-h-[40px] sm:min-h-0"
                onClick={() => navigate(`/analytics?event=${event.id}`)}
              >
                <BarChart3 className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="text-xs sm:text-sm">Analytics</span>
              </Button>
              <Button 
                variant="destructive" 
                size="sm"
                className="min-h-[40px] sm:min-h-0 col-span-2 sm:col-span-1"
                onClick={() => handleDeleteEvent(event.id)}
              >
                <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="text-xs sm:text-sm">Delete</span>
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-background">
        <NavigationBar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-background">
      <NavigationBar />
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Dashboard</h1>
              <p className="text-sm sm:text-base text-muted-foreground">Manage your events and track voting progress</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreateEvent} size="lg" className="touch-manipulation w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                Create Event
              </Button>
            </div>
          </div>
        </div>

        <DashboardQuickStats 
          createdEvents={createdEvents}
          invitedEvents={invitedEvents}
          onCreateEvent={handleCreateEvent}
          userEmail={user?.email}
        />

        <Tabs defaultValue="my-events" className="space-y-4 sm:space-y-6">
          <TabsList className="w-full justify-start overflow-x-auto flex-nowrap">
            <TabsTrigger value="my-events" className="text-xs sm:text-sm whitespace-nowrap">
              My Events ({createdEvents.length})
            </TabsTrigger>
            <TabsTrigger value="invited" className="text-xs sm:text-sm whitespace-nowrap">
              Invited ({invitedEvents.length})
            </TabsTrigger>
            {hasClientPortalAccess() && (
              <TabsTrigger value="clients" className="text-xs sm:text-sm whitespace-nowrap">
                Clients ({clients.length})
              </TabsTrigger>
            )}
            {(hasCustomBranding() || hasAdvancedBranding()) && (
              <TabsTrigger value="templates" className="text-xs sm:text-sm whitespace-nowrap">Templates</TabsTrigger>
            )}
            {(hasBasicCustomRSVP || hasUnlimitedCustomRSVP) && (
              <TabsTrigger value="rsvp-builder" className="text-xs sm:text-sm whitespace-nowrap">RSVP Questions</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="my-events" className="space-y-4">
            {createdEvents.length === 0 ? (
              <DashboardEmptyState 
                variant="no-events"
                onCreateEvent={handleCreateEvent}
              />
            ) : (
              <div className="grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                {createdEvents.map((event) => (
                  <EventCard key={event.id} event={event} isOwner={true} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="invited" className="space-y-4">
            {invitedEvents.length === 0 ? (
              <DashboardEmptyState 
                variant="no-invitations"
                onCreateEvent={handleCreateEvent}
              />
            ) : (
              <div className="grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                {invitedEvents.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            )}
          </TabsContent>

          {hasClientPortalAccess() && (
            <TabsContent value="clients" className="space-y-6">
              <ClientManagement />
              <ClientActivityTimeline />
              <ClientFeedbackManager />
            </TabsContent>
          )}

          {(hasCustomBranding() || hasAdvancedBranding()) && (
            <TabsContent value="templates" className="space-y-4">
              <ProfessionalTemplateManager />
            </TabsContent>
          )}

          {(hasBasicCustomRSVP || hasUnlimitedCustomRSVP) && (
            <TabsContent value="rsvp-builder" className="space-y-4">
            {createdEvents.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <ClipboardList className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No events for RSVP forms</h3>
                  <p className="text-muted-foreground mb-4">
                    Create an event first to build custom RSVP forms with advanced questions and analytics.
                  </p>
                  <Button onClick={handleCreateEvent}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Event
                  </Button>
                </CardContent>
              </Card>
            ) : selectedEventForRSVP ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setSelectedEventForRSVP(null)}
                  >
                    ← Back to Events
                  </Button>
                  <h3 className="text-lg font-semibold">
                    RSVP Builder for {createdEvents.find(e => e.id === selectedEventForRSVP)?.name}
                  </h3>
                </div>
                <CustomRSVPManager 
                  eventId={selectedEventForRSVP} 
                  eventName={createdEvents.find(e => e.id === selectedEventForRSVP)?.name || 'Event'}
                />
              </div>
            ) : (
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      Select an event to build custom RSVP forms with advanced questions and analytics
                    </p>
                  </div>
                  {createdEvents.map((event) => (
                    <Card key={event.id} className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold">{event.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {event.guests.length} guests • {event.eventType === 'rsvp' ? 'RSVP Event' : 'Voting Event'}
                          </p>
                        </div>
                        <Button 
                          variant="outline"
                          onClick={() => setSelectedEventForRSVP(event.id)}
                        >
                          <ClipboardList className="w-4 h-4 mr-2" />
                          Manage RSVP
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
          )}
            </TabsContent>
          )}
        </Tabs>

        {createdEvents.length > 0 && (
          <div className="mt-6 sm:mt-8">
            <UnifiedCalendar 
              events={createdEvents}
              onEventClick={(event) => navigate(`/vote/${event.shareableLink}`)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
